import React, { useEffect, useState } from 'react';
import _ from 'lodash';
import { Matcher, StatusCodeCondition } from '../../../../../../types/rest/dslTypes';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import StatusCodeSelect from '../../../../../../components/transactionEditor/StatusCodeSelect';
import httpStatusCodes, { getStatusCodeItemByCode, HttpStatusCodeItem } from '../../../../../../utils/httpStatusCodes';
import { Button, Form } from 'react-bootstrap';
import BzmIcon from '../../../../../../components/icons/BzmIcon';
import MatcherOperatorDropdown from '../../../../../../components/transactionEditor/MatcherOperatorDropdown';
import { DropdownButtonItem } from '../../../../../../components/buttons/BzDropdownButton';
import { useIsMount } from '../../../../../../hooks/useIsMountHook';
import { allMatcherOperators } from '../../TransactionEditor';
import { conditionalMatchers } from '../../../../../../constants/ui-constants';
import { useTranslation } from 'react-i18next';

interface ConditionalStatusCodesProps {
  isDisabled: boolean;
  conditionalStatusCodes: StatusCodeCondition[];
  onUpdate: (conditionalStatusCodes: StatusCodeCondition[]) => void;
}

//Default status code for fallback values (200 - OK)
const defaultStatusCode = httpStatusCodes[3];

const ConditionalStatusCodes = (props: ConditionalStatusCodesProps) => {
  const [conditions, setConditions] = useState<StatusCodeCondition[]>(props.conditionalStatusCodes);
  const isMount = useIsMount();
  const { t } = useTranslation('transactions');

  //Definition for pseudo-table and column names & sizes
  const table = {
    headerLabels: [
      t('conditionalStatusCodes.code'),
      t('conditionalStatusCodes.if'),
      t('editor.matcherExpression'),
      t('editor.matcherOperatorShort'),
      t('editor.matcherValue'),
    ],
    colSizes: {
      md: [3, 'auto', 3, 2, 2, 1] as any,
      lg: [2, 'auto', 3, 2, 2, 2] as any,
    },
  };

  useEffect(() => {
    if (!isMount) {
      props.onUpdate(conditions);
    }
  }, [conditions]);

  const removeItem = (index: number) => {
    const conditionsUpdate = _.cloneDeep(conditions);
    conditionsUpdate.splice(index, 1);
    setConditions(conditionsUpdate);
  };
  const getActionButtons = (conditionalStatusCode: StatusCodeCondition, index: number) => {
    //Show Add button is visible only for last row element
    const isAddShown = index === _.size(conditions) - 1;
    return (
      <>
        <Button
          title={'Remove Conditional Status Code'}
          onClick={() => removeItem(index)}
          className={'btn-circular btn btn-danger mr-4'}
          type="button"
        >
          <BzmIcon variant={'icon-bzm-delete'} />
        </Button>
        {isAddShown && (
          <Button
            title={'Add new Conditional Status Code'}
            onClick={e => addNew(e)}
            className={'btn-circular btn btn-secondary'}
            type="button"
          >
            <BzmIcon variant={'icon-bzm-add'} />
          </Button>
        )}
      </>
    );
  };

  const getSelectedMatcher = (item: StatusCodeCondition): DropdownButtonItem => {
    const matcherKey = item.matcher.matcherName;
    const matcherTitle = allMatcherOperators[matcherKey];
    return { key: matcherKey, title: matcherTitle };
  };

  const selectMatcherHandler = (matcherItem: DropdownButtonItem, index: number) => {
    const updated = _.cloneDeep(conditions);
    const selectedMatcher = conditionalMatchers.find(item => item === matcherItem.key);
    updated[index].matcher.matcherName = selectedMatcher;
    setConditions(updated);
  };

  /**
   * Update of the key or value
   * @param index
   * @param key
   * @param value
   */
  const onUpdateMatcherDslHandler = (index: number, key: 'key' | 'matchingValue', value: string) => {
    const updated = _.cloneDeep(conditions);
    updated[index].matcher[key] = value;
    setConditions(updated);
  };

  const updateStatusCode = (item: HttpStatusCodeItem, index: number) => {
    const updated = _.cloneDeep(conditions);
    const statusCode: HttpStatusCodeItem = getStatusCodeItemByCode(item.statusCode);
    updated[index].status = statusCode.statusCode;
    updated[index].statusMessage = statusCode.statusMsg;
    setConditions(updated);
  };
  const getRow = (item: StatusCodeCondition, index: number) => {
    return (
      <Row className={'mb-4'} key={'status-code-' + index}>
        <Col lg={table.colSizes.lg[0]} md={table.colSizes.md[0]}>
          <StatusCodeSelect
            isReadOnly={props.isDisabled}
            onSelect={(item: HttpStatusCodeItem) => updateStatusCode(item, index)}
            selectedStatus={getStatusCodeItemByCode(item.status)}
            id="qa-select-status-code"
          />
        </Col>
        <Col lg={table.colSizes.lg[1]} md={table.colSizes.md[1]} className={'text-center'}>
          <strong className={'position-relative'} style={{ top: '5px' }}>
            If
          </strong>
        </Col>
        <Col lg={table.colSizes.lg[2]} md={table.colSizes.md[2]}>
          <Form.Control
            autoComplete={'off'}
            onChange={e => onUpdateMatcherDslHandler(index, 'key', e.target.value)}
            placeholder={t('conditionalStatusCodes.expressionPlaceholder')}
            name={'key'}
            value={item.matcher.key}
          />
        </Col>
        <Col lg={table.colSizes.lg[3]} md={table.colSizes.md[3]}>
          <MatcherOperatorDropdown
            id={'conditional-matcher-' + index}
            operators={conditionalMatchers}
            isReadOnly={props.isDisabled}
            onSelect={item => selectMatcherHandler(item, index)}
            selected={getSelectedMatcher(item)}
            isShortTitle={false}
          />
        </Col>
        <Col lg={table.colSizes.lg[4]} md={table.colSizes.md[4]}>
          <Form.Control
            autoComplete={'off'}
            onChange={e => onUpdateMatcherDslHandler(index, 'matchingValue', e.target.value)}
            placeholder={t('conditionalStatusCodes.valuePlaceholder')}
            name={'value'}
            value={item.matcher.matchingValue}
          />
        </Col>
        <Col lg={table.colSizes.lg[5]} md={table.colSizes.md[5]}>
          {getActionButtons(item, index)}
        </Col>
      </Row>
    );
  };

  /**
   * Adds new conditional status code.
   */
  const addNew = event => {
    event.preventDefault();
    event.stopPropagation();
    const conditionalCodes = _.cloneDeep(conditions);
    const condition: StatusCodeCondition = {
      status: defaultStatusCode.statusCode,
      statusMessage: defaultStatusCode.statusMsg,
      matcher: { key: '', matcherName: Matcher.equals, matchingValue: '' },
    };
    conditionalCodes.push(condition);
    setConditions(conditionalCodes);
  };

  const getPlaceholder = () => {
    return (
      <div className={'mb-4'}>
        <p>
          {t('conditionalStatusCodes.desc')} {t('conditionalStatusCodes.notDefinedYet')}
        </p>
        <Button type="button" className={'btn-circular btn btn-primary'} onClick={addNew}>
          +
        </Button>
      </div>
    );
  };

  const getHeader = () => {
    const mainLabel = <label htmlFor="">Conditional Status Code</label>;

    //WHen placeholder is shown
    if (_.isEmpty(conditions)) {
      return mainLabel;
    }

    //Build header with labels
    return (
      <>
        <p>{t('conditionalStatusCodes.desc')}</p>
        <Row>
          {_.map(table.headerLabels, (headerCol, index) => {
            return (
              <Col key={'header-label-' + index} md={table.colSizes.md[index]} lg={table.colSizes.lg[index]}>
                {table.headerLabels[index] ? (
                  <label style={index == 1 ? { visibility: 'hidden' } : undefined} htmlFor="">
                    {table.headerLabels[index]}
                  </label>
                ) : (
                  undefined
                )}
              </Col>
            );
          })}
        </Row>
      </>
    );
  };

  return (
    <div className={'mt-4'}>
      {getHeader()}
      {!_.isEmpty(conditions) ? conditions.map((item, index) => getRow(item, index)) : getPlaceholder()}
    </div>
  );
};

export default ConditionalStatusCodes;
